import React, { lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
// Minimal coordinate presence - observatory grid
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.015] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '80px 80px'
}} aria-hidden="true" data-id="element-708" />;
export function Decode() {
  const productStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Decoder',
    description: 'A pattern recognition engine for human systems. Reveals structure, loops, constraints, and patterns.',
    brand: {
      '@type': 'Brand',
      name: 'Lazy Monk Labs'
    }
  };
  return <>
      <Helmet data-id="element-709">
        <title data-id="element-710">Decoder — Pattern Recognition Engine | Lazy Monk Labs</title>
        <meta name="description" content="Decoder is a pattern recognition engine for human systems. It does not give advice. It reveals structure." data-id="element-711" />
        <meta name="robots" content="index, follow" data-id="element-712" />
        <link rel="canonical" href="https://lazymonklabs.com/decoder" data-id="element-713" />

        <script type="application/ld+json" data-id="element-714">
          {JSON.stringify(productStructuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-715">
        {/* Navigation */}
        <header data-id="element-716">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-717">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-718">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-719">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-720" />
                <span className="text-base font-mono tracking-tight text-gray-400" data-id="element-721">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-722">
                <Link to="/decodes" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-medium" data-id="element-723">
                  Decodes
                </Link>
                <Link to="/mirrors/work" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-medium" data-id="element-724">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="text-sm text-[#F5F5F5] hover:text-gray-400 transition-colors font-mono" data-id="element-725">
                  Decoder
                </Link>
                <Link to="/careers" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-726">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-727">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16" data-id="element-728">
          {/* 1. HERO */}
          <section className="relative py-32 md:py-40 px-6 overflow-hidden" data-id="element-729">
            <CoordinateGrid data-id="element-730" />
            <div className="relative z-10 max-w-4xl mx-auto space-y-12" data-id="element-731">
              <div className="space-y-6" data-id="element-732">
                <h1 className="text-5xl md:text-7xl font-serif leading-none text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-733">
                  Decoder is the engine.
                </h1>

                <p className="text-xl md:text-2xl text-gray-400 font-light leading-relaxed" data-id="element-734">
                  Decoder is a pattern recognition system that detects
                  structural patterns in human behavior and decision-making.
                </p>
              </div>

              <div className="pt-6" data-id="element-735">
                <p className="text-sm text-gray-600" data-id="element-736">
                  It powers all Decodes and Mirrors at Lazy Monk Labs.
                </p>
              </div>
            </div>
          </section>

          {/* 2. WHAT DECODER IS (AND IS NOT) */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-737">
            <div className="max-w-4xl mx-auto" data-id="element-738">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-739">
                What Decoder is
              </h2>

              <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-740">
                <p data-id="element-741">
                  Decoder is a system that takes raw human signals and converts
                  them into structural pattern code. It does not work with
                  stories, emotions, or advice. It works with structure.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 pt-8" data-id="element-742">
                  <div className="space-y-4" data-id="element-743">
                    <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-744">
                      Decoder is:
                    </p>
                    <div className="space-y-3 text-base" data-id="element-745">
                      <p data-id="element-746">• A pattern recognition engine</p>
                      <p data-id="element-747">• A structural analysis system</p>
                      <p data-id="element-748">• An interpretation substrate</p>
                    </div>
                  </div>

                  <div className="space-y-4" data-id="element-749">
                    <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-750">
                      Decoder is not:
                    </p>
                    <div className="space-y-3 text-base" data-id="element-751">
                      <p data-id="element-752">• Therapy</p>
                      <p data-id="element-753">• Coaching</p>
                      <p data-id="element-754">• Self-help</p>
                      <p data-id="element-755">• Productivity</p>
                      <p data-id="element-756">• Content</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 3. INPUT → ENGINE → OUTPUT */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-757">
            <div className="max-w-4xl mx-auto" data-id="element-758">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-759">
                How Decoder works
              </h2>

              <div className="space-y-12" data-id="element-760">
                {/* Inputs */}
                <div className="space-y-4" data-id="element-761">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-762">
                    Inputs
                  </p>
                  <div className="space-y-3 text-base text-gray-400" data-id="element-763">
                    <p data-id="element-764">• Birth data (as structural anchors)</p>
                    <p data-id="element-765">• Behavioral questions</p>
                    <p data-id="element-766">• Situations</p>
                    <p data-id="element-767">• Logs</p>
                    <p data-id="element-768">• Other signals (future)</p>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex justify-center" data-id="element-769">
                  <div className="text-gray-600 text-2xl" data-id="element-770">↓</div>
                </div>

                {/* Decoder */}
                <div className="space-y-4" data-id="element-771">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-772">
                    Decoder
                  </p>
                  <p className="text-base text-gray-400" data-id="element-773">
                    • Converts signals into Pattern Code
                  </p>
                </div>

                {/* Arrow */}
                <div className="flex justify-center" data-id="element-774">
                  <div className="text-gray-600 text-2xl" data-id="element-775">↓</div>
                </div>

                {/* Outputs */}
                <div className="space-y-4" data-id="element-776">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-777">
                    Outputs
                  </p>
                  <div className="space-y-3 text-base text-gray-400" data-id="element-778">
                    <p data-id="element-779">• Decodes (one-time interpretations)</p>
                    <p data-id="element-780">• Mirrors (continuous interpretations)</p>
                  </div>
                </div>

                {/* Closing line */}
                <div className="pt-8 border-t border-[#2B2B2B]" data-id="element-781">
                  <p className="text-base text-[#F5F5F5]" data-id="element-782">
                    The same engine is used. Only the time horizon and the
                    compiler changes.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 4. WHAT IS PATTERN CODE */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-783">
            <div className="max-w-4xl mx-auto" data-id="element-784">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-785">
                Pattern Code
              </h2>

              <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-786">
                <p data-id="element-787">
                  Pattern Code is an internal, non-human-readable structural
                  representation of how a system tends to operate under
                  pressure, uncertainty, and responsibility.
                </p>

                <div className="pt-6 border-t border-[#2B2B2B]" data-id="element-788">
                  <p className="text-sm text-gray-600" data-id="element-789">
                    You never see Pattern Code directly. You only see its
                    interpretations.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 5. DECODES AND MIRRORS ARE JUST INTERFACES */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-790">
            <div className="max-w-6xl mx-auto" data-id="element-791">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-16 text-center" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-792">
                Decodes and Mirrors are interfaces to the same engine
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-793">
                <div className="space-y-4" data-id="element-794">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-795">
                    Decodes
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-796">
                    One-time interpretations of Pattern Code through specific
                    lenses (self, work, relationships, time).
                  </p>
                </div>

                <div className="space-y-4" data-id="element-797">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-798">
                    Mirrors
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-799">
                    Continuous interpretations of Pattern Code as it shows up
                    over time in real situations.
                  </p>
                </div>
              </div>

              <div className="pt-12 text-center" data-id="element-800">
                <p className="text-lg text-[#F5F5F5]" data-id="element-801">
                  Different interfaces. Same engine.
                </p>
              </div>
            </div>
          </section>

          {/* 6. WHY THIS EXISTS */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-802">
            <div className="max-w-4xl mx-auto" data-id="element-803">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-804">
                Why build this?
              </h2>

              <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-805">
                <p data-id="element-806">
                  Most human problems are not random. They are loops. They
                  repeat. But people cannot see the loop while they are inside
                  it.
                </p>

                <p className="text-[#F5F5F5] pt-6 border-t border-[#2B2B2B]" data-id="element-807">
                  Decoder exists to make structural loops visible.
                </p>
              </div>
            </div>
          </section>

          {/* 7. QUIET CTA */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-808">
            <div className="max-w-4xl mx-auto text-center space-y-8" data-id="element-809">
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-810">
                <Link to="/decodes" className="px-10 py-4 border border-[#F5F5F5] text-[#F5F5F5] text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-811">
                  Explore Decodes
                </Link>
                <Link to="/mirrors/work" className="px-10 py-4 border border-[#F5F5F5] text-[#F5F5F5] text-sm font-medium hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-812">
                  Explore Work Mirror
                </Link>
              </div>

              <div data-id="element-813">
                <Link to="/" className="inline-flex items-center gap-3 text-sm text-gray-600 hover:text-gray-400 transition-colors group" data-id="element-814">
                  <span data-id="element-815">Back to Home</span>
                </Link>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-[#2B2B2B]" data-id="element-816">
          <div className="max-w-6xl mx-auto" data-id="element-817">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8" data-id="element-818">
              <div data-id="element-819">
                <p className="text-sm text-gray-500 font-mono" data-id="element-820">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-821">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex gap-8 text-sm text-gray-500" data-id="element-822">
                <Link to="/decodes" className="hover:text-[#F5F5F5] transition-colors" data-id="element-823">
                  Decodes
                </Link>
                <Link to="/mirrors/work" className="hover:text-[#F5F5F5] transition-colors" data-id="element-824">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-825">
                  Decoder
                </Link>
                <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-826">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-827">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-828">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
}